import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FolderOpen, 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Search,
  File,
  Image as ImageIcon,
  FileCode,
  FileImage
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const ProjectFiles = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.get('/file-manager/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error('Error loading projects');
      console.error(error);
    }
  };

  const loadProjectFiles = async (projectId) => {
    try {
      const response = await api.get(`/file-manager/files/${projectId}`);
      setFiles(response.data);
    } catch (error) {
      toast.error('Error loading files');
      console.error(error);
    }
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    loadProjectFiles(project.project_id);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/file-manager/upload/${selectedProject.project_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success('File uploaded successfully');
      loadProjectFiles(selectedProject.project_id);
      loadProjects(); // Refresh file counts
    } catch (error) {
      toast.error('Error uploading file');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/file-manager/download/${fileId}`, '_blank');
    } catch (error) {
      toast.error('Error downloading file');
      console.error(error);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await api.delete(`/file-manager/files/${fileId}`);
      toast.success('File deleted successfully');
      loadProjectFiles(selectedProject.project_id);
      loadProjects(); // Refresh file counts
    } catch (error) {
      toast.error('Error deleting file');
      console.error(error);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return ImageIcon;
    if (mimeType.includes('pdf')) return FileText;
    if (mimeType.includes('dwg') || mimeType.includes('dxf')) return FileCode;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(file =>
    file.original_filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('projectFiles') || 'Project Files'}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('projectFilesDesc') || 'Manage project folders with drawings, documents, and files'}
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Projects Sidebar */}
        <div className="col-span-3">
          <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
              <FolderOpen className="mr-2 h-5 w-5" />
              {t('projects') || 'Projects'}
            </h3>

            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {projects.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  {t('noProjects') || 'No projects yet'}
                </p>
              ) : (
                projects.map((project) => (
                  <div
                    key={project.project_id}
                    onClick={() => handleSelectProject(project)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedProject?.project_id === project.project_id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500'
                        : 'bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FolderOpen className={`h-5 w-5 ${
                        selectedProject?.project_id === project.project_id
                          ? 'text-blue-600'
                          : 'text-slate-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                          {project.project_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {project.file_count} {t('files') || 'files'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Files Area */}
        <div className="col-span-9">
          {!selectedProject ? (
            <Card className="p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-600">
              <FolderOpen className="mx-auto h-24 w-24 text-slate-300 mb-6" />
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                {t('selectProject') || 'Select a Project'}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                {t('selectProjectDesc') || 'Choose a project from the sidebar to view and manage its files'}
              </p>
            </Card>
          ) : (
            <>
              {/* Toolbar */}
              <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder={t('searchFiles') || 'Search files...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                    <Button
                      onClick={() => document.getElementById('file-upload').click()}
                      disabled={uploading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploading ? (t('uploading') || 'Uploading...') : (t('uploadFile') || 'Upload File')}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Files Grid */}
              {filteredFiles.length === 0 ? (
                <Card className="p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-600">
                  <FileText className="mx-auto h-24 w-24 text-slate-300 mb-6" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                    {searchQuery ? (t('noFilesFound') || 'No files found') : (t('noFilesYet') || 'No files yet')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
                    {searchQuery 
                      ? (t('tryDifferentSearch') || 'Try a different search term')
                      : (t('uploadFirstFile') || 'Upload your first file to get started')
                    }
                  </p>
                  {!searchQuery && (
                    <Button
                      onClick={() => document.getElementById('file-upload').click()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {t('uploadFile') || 'Upload File'}
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredFiles.map((file) => {
                    const FileIcon = getFileIcon(file.mime_type);
                    return (
                      <Card
                        key={file.id}
                        className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <FileIcon className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                              {file.original_filename}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatFileSize(file.file_size)}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(file.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(file.id, file.original_filename)}
                            className="flex-1"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {t('download') || 'Download'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(file.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectFiles;
